import Icons from '../../../../assets/index';
import { ALLOWED_EXTENSIONS, ALLOWED_FILE_TYPES, MAX_FILE_SIZE } from '@/constants/fileUpload';
import { ChangeEvent, useState, useRef, DragEvent, useEffect } from 'react';
import { CustomMultipleFileInputProps, UploadedMultipleFile } from './types/CustomInputsTypes';
import { api } from '@/common/config';
import Modal from '../Modal/Modal';
import DeleteRowPopup from './DeleteRowPopup';
import { DownloadIcon } from 'lucide-react';

import Loader from '@/components/Loader/Loader';

interface UploadedFile {
  blobUrl: string;
  fileName: string;
  fileId: string;
  fileSize: number;
  description?: string;
}

const CustomMultipleFileUpload = ({
  label,
  name,
  onChange,
  required = false,
  accept = '.pdf,.doc,.docx,.xls,.xlsx,.csv,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv',
  maxFileSize = MAX_FILE_SIZE,
  allowedExtensions = ALLOWED_EXTENSIONS,
  allowedFileTypes = ALLOWED_FILE_TYPES,
  isCondition = false,
  isLockedByMe,
  lockedOwner,
  isReviewForm,
  values,
  onFocus,
  isPdf,
  isCancel,
  className,
}: CustomMultipleFileInputProps) => {
  const [files, setFiles] = useState<UploadedMultipleFile[]>([]);
  const [textValue, setTextValue] = useState<string>('');
  const [error, setError] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const [rejectedFile, setRejectedFile] = useState<any | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [downloadingFileId, setDownloadingFileId] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [rowToDelete, setRowToDelete] = useState<string | null>(null);
  const [isDeletePopupOpen, setIsDeletePopupOpen] = useState(false);

  const handleDeleteClick = (fileId: string) => {
    setRowToDelete(fileId);
    setIsDeletePopupOpen(true);
  };

  useEffect(() => {
    if (isCancel) {
      setFiles(values);
    }
  }, [isCancel]);

  const confirmDelete = () => {
    if (rowToDelete) {
      handleRemoveFile(rowToDelete);
    }
    setIsDeletePopupOpen(false);
    setRowToDelete(null);
  };

  const cancelDelete = () => {
    setIsDeletePopupOpen(false);
    setRowToDelete(null);
  };

  useEffect(() => {
    if (values) {
      if (typeof values === 'object' && values) {
        setFiles(values);
        setTextValue('');
      }
    }
  }, [values]);

  const validateFile = (fileToValidate: File) => {
    if (fileToValidate.size > maxFileSize) {
      setError(
        `File size exceeds the ${(maxFileSize / (1024 * 1024)).toFixed(0)} MB limit. Your file is ${(
          fileToValidate.size /
          (1024 * 1024)
        ).toFixed(3)} MB.`
      );
      return false;
    }

    const fileExtension = fileToValidate.name
      .toLowerCase()
      .slice(fileToValidate.name.lastIndexOf('.'));

    if (!allowedExtensions.includes(fileExtension)) {
      setError(`Invalid file type. PDF, DOCX, Excel and CSV files are allowed.`);
      return false;
    }

    if (fileToValidate.type && !allowedFileTypes.includes(fileToValidate.type)) {
      if (!allowedExtensions.includes(fileExtension)) {
        setError('Invalid file format. Please upload a valid PDF, DOCX, Excel and CSV file.');
        return false;
      }
    }

    setError('');
    return true;
  };

  const uploadFileToAzure = async (fileToUpload: File): Promise<UploadedFile | null> => {
    try {
      setIsUploading(true);
      const formData = new FormData();
      formData.append('file', fileToUpload);
      formData.append('containerName', 'uploads');

      const response = await fetch(`${api}/azure-container/upload`, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        throw new Error(`Upload failed with status: ${response.status}`);
      }

      const result = await response.json();

      if (result.status === 'success' && result.data?.blobUrl) {
        return {
          blobUrl: result.data.blobUrl,
          fileName: fileToUpload.name,
          fileId: result.data.fileId || Date.now().toString(),
          fileSize: fileToUpload.size,
          description: '',
        };
      } else {
        throw new Error('Failed to get valid blob URL from response');
      }
    } catch (error) {
      console.error('Error uploading file to Azure:', error);
      setError(
        `Failed to upload file: ${error instanceof Error ? error.message : 'Unknown error'}`
      );
      return null;
    } finally {
      setIsUploading(false);
    }
  };

  // Updated downloadFile function with loading state
  const downloadFile = async (file: UploadedMultipleFile) => {
    try {
      setDownloadingFileId(file.fileId);
      setError(''); // Clear any previous errors

      const blobName = file.blobUrl.split('/').pop();
      if (!blobName) throw new Error('Invalid blob URL');

      // Use the same endpoint but with download action
      const downloadUrl = `${api}/azure-container/download?blobName=${encodeURIComponent(
        blobName
      )}&containerName=documents&action=download`;

      // Create a temporary link element for downloading
      const response = await fetch(downloadUrl);
      if (!response.ok) throw new Error('Download failed');

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);

      const a = document.createElement('a');
      a.href = url;
      a.download = file.fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();

      // Clean up the object URL
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading file:', error);
      setError(
        `Failed to download file: ${error instanceof Error ? error.message : 'Unknown error'}`
      );
    } finally {
      setDownloadingFileId(null);
    }
  };

  const updateParentComponent = (updatedFiles: UploadedFile[], updatedTextValue: string) => {
    onChange({
      value: updatedTextValue,
      files: updatedFiles,
    });
  };

  const handleFileChange = async (e: ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(e.target.files || []);
    const uploadPromises = selectedFiles.map(async (selectedFile) => {
      if (validateFile(selectedFile)) {
        return await uploadFileToAzure(selectedFile);
      } else {
        setRejectedFile(selectedFile);
        return null;
      }
    });

    const uploadResults = await Promise.all(uploadPromises);
    const newUploadedFiles = uploadResults.filter((file): file is UploadedFile => file !== null);

    if (newUploadedFiles.length > 0) {
      setFiles((prevFiles) => {
        const updatedFiles = [...prevFiles, ...newUploadedFiles];
        updateParentComponent(updatedFiles, textValue);
        return updatedFiles;
      });
    }

    if (e.target) e.target.value = '';
  };

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    if (isReviewForm) return;
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragEnter = (e: DragEvent<HTMLDivElement>) => {
    if (isReviewForm) return;
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
    if (isReviewForm) return;
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = async (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);

    const droppedFiles = Array.from(e.dataTransfer.files);
    const uploadPromises = droppedFiles.map(async (droppedFile) => {
      if (validateFile(droppedFile)) {
        return await uploadFileToAzure(droppedFile);
      }
      return null;
    });

    const uploadResults = await Promise.all(uploadPromises);
    const newUploadedFiles = uploadResults.filter((file): file is UploadedFile => file !== null);

    if (newUploadedFiles.length > 0) {
      setFiles((prevFiles) => {
        const updatedFiles = [...prevFiles, ...newUploadedFiles];
        updateParentComponent(updatedFiles, textValue);
        return updatedFiles;
      });
    }
  };

  const handleRemoveFile = (fileId: string) => {
    const updatedFiles = files.filter((file) => file.fileId !== fileId);
    setFiles(updatedFiles);
    updateParentComponent(updatedFiles, textValue);
    setError('');
  };

  const handleDescriptionChange = (fileId: string, newDescription: string) => {
    const updatedFiles = files.map((file) =>
      file.fileId === fileId ? { ...file, description: newDescription } : file
    );
    setFiles(updatedFiles);
    updateParentComponent(updatedFiles, textValue);
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' bytes';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  };

  const handleRemove = () => {
    setRejectedFile(null);
    setError('');
  };

  return (
    <div className={`w-full ${className}`}>
      <div className="mb-2">
        {!isCondition && (
          <label className="block font-semibold text-ds-gray-800">
            {label} {required && <span className="text-red-500">*</span>}
          </label>
        )}
      </div>

      <div
        className={`border w-full rounded-sm p-4 text-center transition-colors custom-border ${
          isDragging ? 'border-blue-500 bg-blue-50' : 'border-ds-gray-200'
        } ${isUploading ? 'opacity-50 pointer-events-none' : ''}`}
        onDragOver={handleDragOver}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => {
          if (!isUploading) {
            fileInputRef.current?.click();
            onFocus?.();
          }
        }}
      >
        <div className="flex flex-col items-center justify-center space-y-2">
          {isUploading ? (
            <div className="flex flex-col items-center justify-center space-y-2">
              <div className="animate-spin rounded-full h-8 w-8 border-2 border-gray-300 border-t-blue-500"></div>
              <p className="text-sm font-semibold text-gray-500">Uploading file...</p>
            </div>
          ) : (
            <>
              <div>
                <Icons.fileUploadIcon />
              </div>
              <p className="text-sm font-semibold text-gray-500">
                Click or drag files to this area to upload
              </p>
              <p className="text-xs text-gray-400">
                Upload PDF, DOCX, Excel or CSV files (.pdf, .docx, .xlsx, .csv). Maximum file size
                allowed is {(maxFileSize / (1024 * 1024)).toFixed(0)}MB per file.
              </p>
            </>
          )}
        </div>
        <input
          type="file"
          name={name}
          className="hidden"
          ref={fileInputRef}
          onChange={handleFileChange}
          accept={accept}
          required={required}
          multiple
          disabled={isUploading || isReviewForm || (lockedOwner !== '' && !isLockedByMe)}
        />
      </div>

      {files.length > 0 && (
        <div className="mt-4 border rounded-[4px] border-blue-2100 overflow-hidden">
          <table className="w-full">
            <thead className="bg-blue-950">
              <tr>
                <th className="px-4 py-3 text-left text-sm border-r text-ds-gray-600 font-semibold tracking-wider">
                  Document Name
                </th>
                <th className="px-4 py-2 text-left text-sm border-r text-ds-gray-600 font-semibold tracking-wider">
                  Description
                </th>
                <th className="px-4 py-2 text-left text-sm border-r text-ds-gray-600 font-semibold tracking-wider">
                  Size
                </th>
                {!isPdf && (
                  <th className="px-4 py-2 text-left text-sm text-ds-gray-600 font-semibold tracking-wider">
                    Actions
                  </th>
                )}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {files.map((file) => (
                <tr key={file.fileId}>
                  <td className="px-4 py-2 border-r">
                    <div className="flex items-center justify-start">
                      <span
                        className={`text-xs text-ds-gray-500 truncate max-w-xs ${isPdf ? 'py-2' : ''}`}
                      >
                        {file.fileName}
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-2 border-r">
                    {isReviewForm || isPdf ? (
                      <span className="text-sm text-ds-gray-500">{file.description || '-'}</span>
                    ) : (
                      <input
                        type="text"
                        value={file.description || ''}
                        onChange={(e) => handleDescriptionChange(file.fileId, e.target.value)}
                        className="w-full py-1 text-ds-gray-500 placeholder:text-ds-gray-300 text-xs rounded focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                        placeholder="Provide context of the document."
                        disabled={(lockedOwner !== '' && !isLockedByMe) || isReviewForm}
                      />
                    )}
                  </td>
                  <td className="px-4 py-2 text-xs text-ds-gray-500 border-r">
                    {formatFileSize(file.fileSize)}
                  </td>

                  {!isPdf && (
                    <td className="px-4 py-2 flex items-center space-x-4">
                      <button
                        onClick={() => downloadFile(file)}
                        className={`text-blue-500 flex items-center justify-center ${
                          downloadingFileId === file.fileId ? 'opacity-50 cursor-not-allowed' : ''
                        }`}
                        type="button"
                        disabled={isUploading || downloadingFileId === file.fileId}
                        title={downloadingFileId === file.fileId ? 'Downloading...' : 'Download'}
                      >
                        {downloadingFileId === file.fileId ? (
                          <div>
                            <Loader />{' '}
                          </div>
                        ) : (
                          <DownloadIcon size={20} />
                        )}
                      </button>

                      <button
                        onClick={() => handleDeleteClick(file.fileId)}
                        className={`text-red-500 hover:text-red-700  ${isReviewForm ? 'opacity-30 cursor-not-allowed' : ''}`}
                        type="button"
                        disabled={
                          isUploading ||
                          downloadingFileId === file.fileId ||
                          (lockedOwner !== '' && !isLockedByMe) ||
                          isReviewForm
                        }
                        title="Delete"
                      >
                        <Icons.trashIcon />
                      </button>
                    </td>
                  )}
                </tr>
              ))}
              {isDeletePopupOpen && (
                <Modal
                  isOpen={isDeletePopupOpen}
                  onClose={cancelDelete}
                  title="Delete Row"
                  size="md"
                  modalName="delete_row_popup_model"
                >
                  <DeleteRowPopup onConfirm={confirmDelete} onCancel={cancelDelete} />
                </Modal>
              )}
            </tbody>
          </table>
        </div>
      )}

      {error && (
        <>
          <div className="flex items-center my-2 rounded-sm bg-ds-red-100 border-ds-gray-200">
            <div className="bg-ds-red-200 px-4 py-2 flex items-center border-l-4 border-ds-red-400 justify-center">
              <Icons.pdfIcon />
            </div>
            <div className="flex-1 px-4 py-2">
              <p className="text-sm font-medium text-gray-800 truncate"> {rejectedFile?.name}</p>
              <p className="text-xs text-gray-500">
                {rejectedFile ? formatFileSize(rejectedFile?.size) : ''}
              </p>
            </div>
            <button onClick={handleRemove} className="px-4 text-ds-gray-500 hover:text-ds-gray-800">
              <Icons.closeIcon />
            </button>
          </div>
          <p className="text-xs text-ds-red-500 font-normal pl-2">{error}</p>
        </>
      )}
    </div>
  );
};

export default CustomMultipleFileUpload;
